import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { createPeer, getPeer } from '../lib/peer';
import {
  generateRSAIdentityKeyPair,
  exportRSAPublicKey,
  exportRSAPrivateKey,
  importRSAPublicKey,
  importRSAPrivateKey,
  setRSAIdentityKeys // 🔥 you must have setter in peer.js
} from '../lib/peer';
import pb from '../lib/pb';

const AuthContext = createContext();

export function AuthProvider({ children }) {

  const [user, setUser] = useState(pb.authStore.model ?? null);
  const [isReady, setIsReady] = useState(false);
  const [peerReady, setPeerReady] = useState(false);

  const rsaInitRef = useRef(false);
  const peerInitRef = useRef(false);
  const peerInstanceRef = useRef(null);

  /* =====================================================
     INIT ON APP LOAD
  ===================================================== */
  useEffect(() => {

    const init = async () => {
      if (pb.authStore.isValid) {
        setUser(pb.authStore.model);
        await ensureRSAIdentity();
        await createAndRegisterPeer();
      }
      setIsReady(true);
    };

    init();

    const removeListener = pb.authStore.onChange(async () => {

      const model = pb.authStore.model ?? null;
      setUser(model);

      if (!model?.id) {
        setPeerReady(false);
        peerInitRef.current = false;
        destroyPeerHard();
        return;
      }

      if (!rsaInitRef.current) {
        await ensureRSAIdentity();
      }

      if (!peerInitRef.current) {
        await createAndRegisterPeer();
      }

    });

    return () => {
      try { removeListener(); } catch {}
      destroyPeerHard();
    };

  }, []);

  /* =====================================================
     HARD PEER CLEANUP
  ===================================================== */
  const destroyPeerHard = () => {
    try {
      const existing = getPeer();
      if (existing) {
        existing.removeAllListeners();
        existing.disconnect();
        existing.destroy();
      }

      if (peerInstanceRef.current) {
        peerInstanceRef.current.removeAllListeners();
        peerInstanceRef.current.disconnect();
        peerInstanceRef.current.destroy();
        peerInstanceRef.current = null;
      }

    } catch {}
  };

  /* =====================================================
     ENSURE RSA IDENTITY (FIXED)
  ===================================================== */
  const ensureRSAIdentity = async () => {

    const model = pb.authStore.model;
    if (!model?.id) return;

    if (rsaInitRef.current) return;

    rsaInitRef.current = true;

    const fresh = await pb.collection('users').getOne(model.id);

    try {

      // ✅ CASE 1: RSA already exists in DB → IMPORT IT
      if (fresh.rsa_public_key && fresh.rsa_private_key) {

        console.log("🔐 Importing existing RSA identity...");

        const publicKey = await importRSAPublicKey(fresh.rsa_public_key);
        const privateKey = await importRSAPrivateKey(fresh.rsa_private_key);

        setRSAIdentityKeys(publicKey, privateKey);

        console.log("✅ RSA identity restored into WebCrypto.");
        return;
      }

      // ✅ CASE 2: First time ever → GENERATE
      console.log("🔐 Generating RSA identity for user (FIRST TIME ONLY)...");

      const kp = await generateRSAIdentityKeyPair();

      const publicKeyExport = await exportRSAPublicKey(kp.publicKey);
      const privateKeyExport = await exportRSAPrivateKey(kp.privateKey);

      await pb.collection('users').update(model.id, {
        rsa_public_key: publicKeyExport,
        rsa_private_key: privateKeyExport
      });

      setRSAIdentityKeys(kp.publicKey, kp.privateKey);

      const refreshed = await pb.collection('users').authRefresh();
      setUser(refreshed.record);

      console.log("✅ RSA identity generated + stored permanently.");

    } catch (err) {
      console.error("❌ RSA identity setup failed:", err);
      rsaInitRef.current = false;
    }
  };

  /* =====================================================
     CREATE & REGISTER PEER
  ===================================================== */
  const createAndRegisterPeer = async () => {

    if (!pb.authStore.model?.id) return;
    if (peerInitRef.current) return;

    peerInitRef.current = true;
    setPeerReady(false);

    const myPeerId = `u_${pb.authStore.model.id}`;

    try {

      destroyPeerHard();
      await new Promise(r => setTimeout(r, 1200));

      const peer = await createPeer(myPeerId);
      peerInstanceRef.current = peer;

      console.log("✅ Peer fully ready:", myPeerId);
      setPeerReady(true);

      await pb.collection('users').update(
        pb.authStore.model.id,
        { peerId: myPeerId }
      );

      peer.on("close", () => {
        setPeerReady(false);
        peerInitRef.current = false;
      });

      peer.on("disconnected", () => {
        setPeerReady(false);
        peerInitRef.current = false;
      });

      peer.on("error", async (err) => {

        console.error("Peer error:", err);

        if (err?.type === "unavailable-id") {
          peerInitRef.current = false;
          await new Promise(r => setTimeout(r, 2000));
          createAndRegisterPeer();
          return;
        }

        setPeerReady(false);
        peerInitRef.current = false;
      });

    } catch (err) {
      console.warn("⚠️ Peer init failed:", err);
      setPeerReady(false);
      peerInitRef.current = false;
    }
  };

  /* =====================================================
     LOGIN
  ===================================================== */
  const login = async (identity, password) => {
    const authData = await pb.collection('users')
      .authWithPassword(identity.trim(), password);

    setUser(pb.authStore.model);
    return authData;
  };

  /* =====================================================
     SIGNUP
  ===================================================== */
  const signup = async ({ username, email, password }) => {

    await pb.collection('users').create({
      name: username,
      email,
      password,
      passwordConfirm: password,
    });

    const authData = await pb.collection('users')
      .authWithPassword(email, password);

    setUser(pb.authStore.model);
    return authData;
  };

  /* =====================================================
     LOGOUT
  ===================================================== */
  const logout = () => {

    destroyPeerHard();

    setPeerReady(false);
    rsaInitRef.current = false;
    peerInitRef.current = false;

    pb.authStore.clear();
    setUser(null);
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        login, 
        signup, 
        logout, 
        isReady,
        peerReady
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);